import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { lastValueFrom } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { selectRouteData } from 'app/_state/router/router-selectors';
import { AppRouterState } from 'app/_state/router/router-state';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { AppStateStorage } from 'core/state/app-state-storage';
import { StoreNames } from 'core/state/app-state.service';
import { CreateEntityDialog } from 'entity/dialogs/create-entity/create-entity.dialog';
import { getSafe } from 'helpers';

import { CAMPAIGN_HEADER_CREATE_ENTITY_CLICKED } from './create-entity-clicked.action';
import { selectCurrentCampaign } from 'app/_state/campaign-context/campaign-context-selectors';

export enum AddEntityButtonClickBehavior {
  OpenDialog = 'OpenDialog', // default
  EmitEvent = 'EmitEvent'
}

@Component({
  selector: 'app-campaign-header',
  templateUrl: './campaign-header.component.html',
})
export class CampaignHeaderComponent extends WithDisposableSubject() implements OnInit {
  searchExpanded = false;
  appStorage = new AppStateStorage();
  showEvents = false;

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.altKey && event.shiftKey && event.key === 'A') {
     this.addNewEntityClicked();
    }
  }

  // When route data contains newEntityButtonHidden
  // it will be hidden. This is used on quick action
  // to avoid showing two add entity buttons.
  newEntityButtonHidden$ = this.store.select(
    selectRouteData
  ).pipe(
    map(data => data.newEntityButtonHidden)
  );

  // used to override the default behavior of opening a dialog
  addEntityClickBehavior$ = this.store.select(
    selectRouteData
  ).pipe(
    map(data => data.campaignHeaderAddEntityClickBehavior || AddEntityButtonClickBehavior.OpenDialog)
  );


  get campaignId() {
    return getSafe(() => this.route.snapshot.queryParams['campaignId'], null);
  }

  get clientQueryId() {
    return getSafe(() => this.route.snapshot.queryParams['clientQueryId'], null);
  }

  constructor(
    private _breakpointObserver: BreakpointObserver,
    private _cd: ChangeDetectorRef,
    private dialog: MatDialog,
    private store: Store<AppRouterState>,
    private route: ActivatedRoute,
    private router: Router
  ) {
    super();
    this.store.select(
      selectCurrentCampaign
    ).pipe(
      takeUntil(this.disposer)
    ).subscribe(campaign => {
      this.showEvents = campaign?.calendarEventsEnabled;
    });
  }

  ngOnInit(): void {
    this._breakpointObserver.observe([
      '(min-width: 1100px)'
    ]).subscribe((result) => {
      if (result.matches) {
        this.maybeExpandSearch(true);
      } else {
        this.maybeExpandSearch(false);
      }
    });
  }

  maybeExpandSearch(maybe: boolean) {
    this.searchExpanded = maybe;
    this._cd.markForCheck();
  }

  async addNewEntityClicked() {
    const behavior = await lastValueFrom(this.addEntityClickBehavior$.pipe(take(1)));
    // Emit an NgRx event that the add entity button was clicked
    // so that it can be handled elsewhere.
    if (behavior === AddEntityButtonClickBehavior.EmitEvent) {
      return this.store.dispatch(CAMPAIGN_HEADER_CREATE_ENTITY_CLICKED());
    }

    return this.openCreateEntityDialog();
  }

  openCreateEntityDialog() {
    const dialogRef = CreateEntityDialog.open(this.dialog, {
      data: {
        campaignId: this.campaignId
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.id) {
        this.goToEntity(result);
      }
    });
  }

  goToEntity(entity: { id: string }) {
    this.router.navigate(['/entity/view', entity.id], {
      queryParams: { campaignId: this.campaignId }
    });
  }

  /**
   * Returns true if given url part
   * matches the current url.
   *
   * @param url Part to match
   */
  isLinkActive(url: string) {
    return this.router.url.includes(url);
  }

}
